import axios, { AxiosRequestConfig } from 'axios';
import Environment from './Environment';
import XhrRequestError from './XhrRequestError';
import XhrRequestCache from './XhrRequestCache';
import { getDeviceData } from './DeviceDetector';
import XhrErrorListener from './XhrErrorListener';


import { INavLinkGroup, INavLink } from '@fluentui/react/lib/Nav';


const progressKey = 'progress';

interface IProgressEvent {
  loaded: number;
  total: number;
  type: string;
}

interface InavLink extends INavLink{
  code: string;
  parentCode: string | null;
  active: string | null;
  classname:  string | null;
  automationid: string | null;
}

export interface IMenugroup extends INavLinkGroup{
  links:InavLink[]
}

export type UploadTracker = (loaded: number, total: number) => unknown;

interface IUnauthConfig {
  uploadTracker?: UploadTracker;
  abortController?: AbortController;
  headers?: { [index: string]: string };
}

class XhrRequestHandler {
  private listeners: XhrErrorListener[];

  constructor(private environment: Environment, private cache: XhrRequestCache) {
    this.listeners = [];
  }

  /**
   * Envía métricas de error a AWS RUM de forma no bloqueante
   */
  private sendAWSRUMError(error: XhrRequestError, url: string, method: string): void {
    // PRIMERA CAPA: setTimeout para ejecutar en el siguiente tick del event loop
    setTimeout(() => {
      // SEGUNDA CAPA: Promesa que se resuelve inmediatamente pero se ejecuta de forma asíncrona
      Promise.resolve().then(async () => {
        try {
          // Verificar si AWS RUM está disponible globalmente
          if (typeof window !== 'undefined' && (window as any).awsRum) {
            const awsRum = (window as any).awsRum;
            
            // Obtener detalles adicionales del error de forma segura
            let responseData: unknown = null;
            let errorStack = 'No stack trace available';
            
            try {
              responseData = error.responseData();
              errorStack = error.stack || 'No stack trace available';
            } catch (dataError) {
              console.debug('Error obteniendo datos del error para RUM:', dataError);
            }
            
            // Preparar metadatos de forma no bloqueante
            const errorMetadata = await this.buildErrorMetadataSafe(error, url, method, responseData, errorStack);
            
            // ENVIAR EVENTOS DE FORMA NO BLOQUEANTE
            this.sendRUMEventsNonBlocking(awsRum, error, errorMetadata, responseData, url, method);
            
          } else {
            console.debug('AWS RUM no está disponible para envío de errores');
          }
        } catch (rumError) {
          // TERCERA CAPA: Silenciar cualquier error sin afectar la aplicación
          console.debug('Error al enviar métricas a AWS RUM:', rumError);
        }
      }).catch((promiseError) => {
        // CUARTA CAPA: Capturar errores de la promesa
        console.debug('Error en promesa de AWS RUM:', promiseError);
      });
    }, 0); // Ejecutar en el siguiente tick
  }

  /**
   * Construye metadatos de error de forma segura y no bloqueante
   */
  private async buildErrorMetadataSafe(error: XhrRequestError, url: string, method: string, responseData: unknown, errorStack: string): Promise<any> {
    return new Promise((resolve) => {
      try {
        const metadata = {
          // Información básica de la petición
          url: this.truncateString(url, 1000),
          method: method,
          timestamp: new Date().toISOString(),
          
          // Detalles del error personalizado
          errorName: this.truncateString(error.name, 255),
          errorMessage: this.truncateString(error.message, 1000),
          errorStack: this.truncateString(errorStack, 3000),
          
          // Información de la API
          apiErrorCode: error.getApiErrorCode(),
          apiErrorMessage: this.truncateString(error.getApiErrorMessage(), 1000),
          httpStatusCode: this.getHttpStatusFromError(error),
          
          // DATOS DE RESPUESTA COMPLETOS
          responseBody: responseData ? this.sanitizeResponseData(responseData) : null,
          
          // Información del contexto (reducida)
          deviceInfo: this.truncateString(getDeviceData(), 300),
          userAgent: this.truncateString(navigator.userAgent, 300),
          sessionId: this.truncateString(this.getSessionId(), 100),
          userId: this.truncateString(this.getUserId(), 100),
          
          // Información adicional de la aplicación
          currentUrl: this.truncateString(window.location.href, 500),
          referrer: this.truncateString(document.referrer || 'direct', 300),
          
          // Flags de estado del error
          isAuthError: error.isAuthenticationRequiredError(),
          isCancelledError: error.isCancel(),
          
          // Timing
          requestTime: new Date().getTime()
        };
        resolve(metadata);
      } catch (metadataError) {
        console.debug('Error construyendo metadatos:', metadataError);
        resolve({}); // Resolver con objeto vacío si hay error
      }
    });
  }

  /**
   * Envía eventos a RUM de forma completamente no bloqueante
   */
  private sendRUMEventsNonBlocking(awsRum: any, error: XhrRequestError, errorMetadata: any, responseData: unknown, url: string, method: string): void {
    // Enviar cada evento en su propio setTimeout para máxima no-bloqueo
    
    // Evento 1: Error principal
    setTimeout(() => {
      try {
        awsRum.recordError(error, errorMetadata);
      } catch (e) { console.debug('Error enviando recordError:', e); }
    }, 0);
    
    // Evento 2: API error con response
    setTimeout(() => {
      try {
        const apiErrorEvent = {
          errorType: this.truncateString(error.name, 255),
          endpoint: this.truncateString(url, 500),
          httpMethod: method,
          statusCode: error.getApiErrorCode(),
          httpStatus: this.getHttpStatusFromError(error),
          serverResponse: responseData ? this.sanitizeResponseData(responseData) : 'No response data',
          isAuthError: error.isAuthenticationRequiredError(),
          isCancelled: error.isCancel(),
          timestamp: new Date().toISOString(),
          apiErrorDetails: error.getApiErrorMessage() || 'No API error message'
        };
        
        console.log('🔍 AWS RUM - Enviando evento api_error_with_response:', apiErrorEvent);
        awsRum.recordEvent('api_error_with_response', apiErrorEvent);
      } catch (e) { console.debug('Error enviando api_error_with_response:', e); }
    }, 1);
    
    // Evento 3: Error 500 específico
    setTimeout(() => {
      try {
        if (this.getHttpStatusFromError(error) === 500) {
          const serverErrorEvent = {
            endpoint: this.truncateString(url, 800),
            method: method,
            fullResponse: responseData ? this.sanitizeResponseData(responseData) : 'No response',
            timestamp: new Date().toISOString(),
            sessionId: this.truncateString(this.getSessionId(), 100)
          };
          
          console.log('🚨 AWS RUM - Enviando evento server_error_500:', serverErrorEvent);
          awsRum.recordEvent('server_error_500', serverErrorEvent);
        }
      } catch (e) { console.debug('Error enviando server_error_500:', e); }
    }, 2);
    
    // Evento 4: Test event (solo en desarrollo)
    setTimeout(() => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ AWS RUM - Enviando evento de prueba simple');
          awsRum.recordEvent('test_custom_event', {
            message: 'Test event to verify custom events work',
            timestamp: new Date().toISOString(),
            url: this.truncateString(url, 200)
          });
        }
      } catch (e) { console.debug('Error enviando test_custom_event:', e); }
    }, 3);
    
    // Log de debug final
    setTimeout(() => {
      try {
        console.debug('Error completo enviado a AWS RUM:', {
          url: this.truncateString(url, 100),
          method,
          errorType: error.name,
          statusCode: error.getApiErrorCode(),
          hasResponseData: !!responseData,
          responsePreview: responseData ? this.truncateString(this.sanitizeResponseData(responseData) || '', 200) : 'No response'
        });
      } catch (e) { console.debug('Error en log de debug:', e); }
    }, 4);
  }

  /**
   * Trunca una cadena para respetar los límites de AWS RUM
   */
  private truncateString(str: string, maxLength: number): string {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
  }

  /**
   * Sanitiza y convierte cualquier tipo de datos de respuesta a string de manera genérica
   */
  private sanitizeResponseData(responseData: unknown): string | null {
    try {
      // Si es null o undefined
      if (responseData === null) return 'null';
      if (responseData === undefined) return 'undefined';
      
      // Si ya es string, simplemente truncar (más espacio para respuestas)
      if (typeof responseData === 'string') {
        return this.truncateString(responseData, 4000);
      }
      
      // Si es número, booleano o función, convertir directamente
      if (typeof responseData === 'number' || 
          typeof responseData === 'boolean' || 
          typeof responseData === 'function') {
        return this.truncateString(String(responseData), 4000);
      }
      
      // Si es array, object, etc. - convertir a JSON de manera segura
      if (typeof responseData === 'object') {
        // Intentar JSON.stringify con replacer para manejar casos edge
        const jsonString = JSON.stringify(responseData, this.getSafeReplacer(), 2);
        return this.truncateString(jsonString, 4000); // Más espacio para JSON de respuesta
      }
      
      // Fallback: convertir a string usando String()
      return this.truncateString(String(responseData), 4000);
      
    } catch (stringifyError) {
      // Si JSON.stringify falla, intentar toString() nativo
      try {
        const toStringResult = Object.prototype.toString.call(responseData);
        return this.truncateString(`Error serializing: ${toStringResult}`, 1000);
      } catch (toStringError) {
        // Último recurso: información básica del tipo
        return `Error serializing response data - Type: ${typeof responseData}`;
      }
    }
  }

  /**
   * Replacer seguro para JSON.stringify que maneja todos los casos edge
   */
  private getSafeReplacer() {
    const seen = new WeakSet();
    
    return (key: string, value: any) => {
      // Manejar valores null y undefined
      if (value === null) return 'null';
      if (value === undefined) return 'undefined';
      
      // Manejar tipos primitivos
      if (typeof value !== 'object') return value;
      
      // Manejar referencias circulares
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
      
      // Manejar casos especiales de objetos
      try {
        // Error objects necesitan tratamiento especial
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack ? this.truncateString(value.stack, 1000) : undefined
          };
        }
        
        // Elementos DOM
        if (value instanceof Element || value instanceof Node) {
          return `[DOM Element: ${value.constructor.name}]`;
        }
        
        // Funciones
        if (typeof value === 'function') {
          return `[Function: ${value.name || 'anonymous'}]`;
        }
        
        // Dates
        if (value instanceof Date) {
          return value.toISOString();
        }
        
        // RegExp
        if (value instanceof RegExp) {
          return value.toString();
        }
        
        // Arrays muy largos
        if (Array.isArray(value) && value.length > 100) {
          return [...value.slice(0, 100), `... ${value.length - 100} more items`];
        }
        
        // Objetos con muchas propiedades
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          const keys = Object.keys(value);
          if (keys.length > 50) {
            const limitedObj: any = {};
            keys.slice(0, 50).forEach(k => limitedObj[k] = value[k]);
            limitedObj['...'] = `${keys.length - 50} more properties`;
            return limitedObj;
          }
        }
        
        return value;
        
      } catch (error) {
        return `[Error processing ${typeof value}]`;
      }
    };
  }

  private getHttpStatusFromError(error: XhrRequestError): number {
    if (error.isAuthenticationRequiredError()) return 401;
    
    // Intentar obtener el status HTTP del error original
    const responseData = error.responseData();
    if (responseData && typeof responseData === 'object' && 'status' in responseData) {
      return (responseData as any).status;
    }
    
    return error.getApiErrorCode() || -1;
  }

  private getSessionId(): string {
    // Obtener session ID de sessionStorage o generar uno temporal
    return sessionStorage.getItem('sessionId') || 'session-' + Date.now();
  }

  private getUserId(): string {
    // Obtener user ID de localStorage o usar 'anonymous'
    return localStorage.getItem('userId') || 'anonymous';
  }

  clearCache() {
    this.cache.clear();
  }

  clearCacheOf(url: string) {
    this.cache.clearOf(url);
  }

  subscribe(aListener: XhrErrorListener) {
    this.assertIsNotSubscribed(aListener);
    this.listeners.push(aListener);
  }

  private assertIsNotSubscribed(aListener: XhrErrorListener) {
    if (this.isSubscribed(aListener)) {
      throw new Error('The listener is already subscribed');
    }
  }

  private isSubscribed(aListener: XhrErrorListener) {
    return this.listeners.includes(aListener);
  }

  unsubscribe(aListener: XhrErrorListener) {
    this.listeners = this.listeners.filter((listener) => listener !== aListener);
  }

   /**
 * Método GET para el menu.
 */
async simpleGetWithCache<T>(url: string): Promise<IMenugroup[]> {
  
  return this.cache.getValue(url, async (url) => {
    try {
      const requestUrl = this.requestUrl(url);
      const defaultConfig: AxiosRequestConfig = { headers: this.defaultHeaders(), withCredentials: true };
      const response = await axios.get<T>(requestUrl, defaultConfig);

      response.data[0].links.forEach((data) => {
        if (data.iconProps) {
          if (typeof data.iconProps === 'string') {
              data.iconProps = JSON.parse(data.iconProps);
          }
        }

        data.links?.forEach((link) => {
          if (link.iconProps && typeof link.iconProps === 'string') {
              link.iconProps = JSON.parse(link.iconProps);
          }
        });
      });
     return response.data;
    } catch (error) {
      const processedError = XhrRequestError.fromUnknown(error);
      
      // Enviar error a AWS RUM de forma no bloqueante
      this.sendAWSRUMError(processedError, url, 'GET');
      
      this.listeners.forEach((listener) => listener.receiveError(processedError));
      throw processedError;
    }
  });
}

  /**
   * @throws {XhrRequestError}
   */
  async get<T>(url: string): Promise<T> {
    return this.cache.getValue(url, (url) => {
      return this.doThrowingRequestError<T>(url, async (fullUrl, config) => {
        return (await axios.get<T>(fullUrl, config)).data;
      }, 'GET');
    });
  }


  /**
   * Método GET sin cache y sin headers personalizados.
   * @throws {XhrRequestError}
   */
  async simpleGet<T>(url: string): Promise<T> {
    const fullUrl = this.requestUrl(url);
    try {
      const response = await axios.get<T>(fullUrl);
      return response.data;
    } catch (error) {
      const processedError = XhrRequestError.fromUnknown(error);
      
      // Enviar error a AWS RUM de forma no bloqueante
      this.sendAWSRUMError(processedError, url, 'GET');
      
      this.listeners.forEach((listener) => listener.receiveError(processedError));
      throw processedError;
    }
  }


  /**
   * @throws {XhrRequestError}
   */
  async post<T = unknown, S = unknown>(url: string, data?: S): Promise<T> {
    const requestBody = data === undefined ? {} : data;
    return this.doThrowingRequestError(
        url,
        async (fullUrl, config) => (await axios.post<T>(fullUrl, requestBody, config)).data,
        'POST'
    );
  }

/**
 * @throws {XhrRequestError}
 */
async postss<T = unknown, S = unknown>(
  url: string,
  data?: S,
  headers?: Record<string, string>
): Promise<T> {
  const requestBody = data === undefined ? {} : data;

  const defaultConfig: AxiosRequestConfig = {
    headers,
    withCredentials: true,
  };
  const fullUrl = this.requestUrl(url);
  try {
    const response = await axios.post<T>(fullUrl, requestBody, defaultConfig);
    return response.data;
  } catch (error) {
    const processedError = XhrRequestError.fromUnknown(error);
    
    // Enviar error a AWS RUM de forma no bloqueante
    this.sendAWSRUMError(processedError, url, 'POST');
    
    this.listeners.forEach((listener) => listener.receiveError(processedError));
    throw processedError;
  }
}

/**
 * @throws {XhrRequestError}
 */
async getheader<T>(url: string, headers: Record<string, string>): Promise<T> {
  const defaultConfig: AxiosRequestConfig = {
    headers: { ...this.defaultHeaders(), ...headers },
    withCredentials: true,
  };
  const fullUrl = this.requestUrl(url);
    try {
    const response = await axios.get<T>(fullUrl, defaultConfig);
    return response.data;
  } catch (error) {
    const processedError = XhrRequestError.fromUnknown(error);
    
    // Enviar error a AWS RUM de forma no bloqueante
    this.sendAWSRUMError(processedError, url, 'GET');
    
    this.listeners.forEach((listener) => listener.receiveError(processedError));
    throw processedError;
  }
}

  async getBlob(url: string, headers?: Record<string, string>): Promise<Blob> {
    const fullUrl = this.requestUrl(url);
    try {
      const response = await axios.get(fullUrl, {
        headers: { ...this.defaultHeaders(), ...headers },
        withCredentials: true,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      const processedError = XhrRequestError.fromUnknown(error);
      this.sendAWSRUMError(processedError, url, 'GET');
      this.listeners.forEach((listener) => listener.receiveError(processedError));
      throw processedError;
    }
  }

  async getBlobWithFilename(url: string, headers?: Record<string, string>, namefileDefault?: string): Promise<{ blob: Blob; filename: string }> {
    const fullUrl = this.requestUrl(url);
    try {
      const response = await axios.get(fullUrl, {
        headers: { ...this.defaultHeaders(), ...headers },
        withCredentials: true,
        responseType: 'blob',
      });

      const disposition: string = response.headers['content-disposition'] ?? '';
      const match = disposition.match(/filename="?([^"]+)"?/);

      const filename = match ? match[1] : namefileDefault || 'descarga';
      return { blob: response.data, filename };
    } catch (error) {
      const processedError = XhrRequestError.fromUnknown(error);
      this.sendAWSRUMError(processedError, url, 'GET');
      this.listeners.forEach((listener) => listener.receiveError(processedError));
      throw processedError;
    }
  }


  /**
   * @throws {XhrRequestError}
   */
  async put<T, S>(
      url: string,
      data: S,
      customHeaders?: Record<string, string>
    ): Promise<T> {
      return this.doThrowingRequestError(
        url,
        async (fullUrl) => {
          const config: AxiosRequestConfig = {
            headers: {
              ...this.defaultHeaders(),
              ...customHeaders,
            },
            withCredentials: true,
          };
          return (await axios.put<T>(fullUrl, data, config)).data;
        },
        'PUT'
      );
    }

  /**
   * @throws {XhrRequestError}
   */
  async patch<T = unknown, S = unknown>(url: string, data: S): Promise<T> {
    return this.doThrowingRequestError(
        url,
        async (fullUrl, config) => (await axios.patch<T>(fullUrl, data, config)).data,
        'PATCH'
    );
  }

  /**
   * @throws {XhrRequestError}
   */
  async unauthenticatedPutForUpload<T, S>(
      url: string,
      data: S,
      { uploadTracker, abortController, headers }: IUnauthConfig
  ): Promise<T> {
    console.log(data);
    
    // Verificar si data es un Blob o File y tiene tamaño 0
    if ((data instanceof Blob || data instanceof File) && data.size === 0) {
      throw new XhrRequestError('EmptyFileUpload', 'El archivo subido está vacío');
    }

    return this.doThrowingRequestError(
        url,
        async (fullUrl) =>
            (
                await axios.put<T>(fullUrl, data, {
                  onUploadProgress: (event: IProgressEvent) => {
                    if (event.type !== progressKey || !uploadTracker) return;
                    uploadTracker(event.loaded, event.total);
                  },
                  signal: abortController && abortController.signal,
                  headers,
                })
            ).data,
        'PUT'
    );
  }

  /**
   * @throws {XhrRequestError}
   */
  delete<T = unknown>(url: string): Promise<T> {
    return this.doThrowingRequestError(
        url,
        async (fullUrl, config) => (await axios.delete<T>(fullUrl, config)).data,
        'DELETE'
    );
  }

  navigateTo(url: string) {
    return this.openUrl(url, false);
  }

  open(url: string) {
    return this.openUrl(url, true);
  }

  /**
   * @throws {XhrRequestError}
   */
  private async doThrowingRequestError<T>(
      url: string,
      closure: (requestUrl: string, config?: AxiosRequestConfig) => Promise<T>,
      method: string = 'HTTP'
  ): Promise<T> {
    try {
      const requestUrl = this.requestUrl(url);
      const defaultConfig: AxiosRequestConfig = { headers: this.defaultHeaders(), withCredentials: true };

      return await closure(requestUrl, defaultConfig);
    } catch (error) {
      const processedError = XhrRequestError.fromUnknown(error);
      
      // Enviar error a AWS RUM de forma no bloqueante
      this.sendAWSRUMError(processedError, url, method);
      
      this.listeners.forEach((listener) => listener.receiveError(processedError));
      throw processedError;
    }
  }

  requestUrl(url: string) {
    return this.isRelative(url) ? this.environment.apiUrl(url) : url;
  }

  private isRelative(url: string) {
    return !url.startsWith('http');
  }

  private defaultHeaders() {
    const headers: { [index: string]: string } = {};
    headers['X-Device-Info'] = getDeviceData();
    headers['Time-Zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return headers;
  }

  private openUrl(url: string, newPage: boolean) {
    const destination = encodeURI(this.requestUrl(url));
    if (newPage) {
      window.open(destination, '_blank');
    } else {
      window.location.assign(destination);
    }
  }

}

export default XhrRequestHandler;
export { XhrRequestError };

export interface QueryParam {
  name: string;
  value?: string | boolean | number;
}

export const getQueryParamsExpression = (params?: QueryParam[]): string => {
  if (!params) return '';

  let query = '';
  let separator = '?';
  params.forEach((param) => {
    if (
        param.name.length > 0 &&
        ((typeof param.value == 'string' && param.value.length > 0) ||
            typeof param.value == 'boolean' ||
            typeof param.value == 'number')
    ) {
      if (typeof param.value == 'boolean') param.value = param.value ? '1' : '0';
      query = `${query}${separator}${param.name}=${encodeURIComponent(param.value)}`;
      separator = '&';
    }
  });
  return query;
};

export interface StatusResponseJSON<T> {
  data: T;
  statusCode: number;
}
